import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { getServerQueryOptions } from "@/app/servers/api/get-server";
import { useOAuthAuthorize } from "@/app/servers/hooks/use-oauth-authorize";
import { getScopesSupported, type Server } from "@/app/servers/types";
import { useResourceScope } from "@/lib/resource-context";
import { OAuthAuthorizationScreen } from "./oauth-authorization-screen";

type OAuthAuthorizationSheetProps = {
  server: Server;
  onAuthComplete?: () => void;
  children: React.ReactElement;
};

export const OAuthAuthorizationSheet = ({
  server,
  onAuthComplete,
  children,
}: OAuthAuthorizationSheetProps) => {
  const scope = useResourceScope();
  const [open, setOpen] = useState(false);
  // null = user hasn't interacted, default to all supported scopes
  const [userSelectedScopes, setUserSelectedScopes] = useState<string[] | null>(
    null
  );

  // Fetch full server detail when the sheet is open. The list API may omit
  // supported_scopes, but the detail API always includes them.
  const { data: serverDetail } = useQuery({
    ...getServerQueryOptions({ id: server.id, scope }),
    enabled: open,
  });
  const scopesSupported = getScopesSupported(serverDetail ?? server);
  const selectedScopes = userSelectedScopes ?? scopesSupported ?? [];

  const {
    authorizationUrl,
    authorize,
    reset: resetAuth,
    isPolling,
  } = useOAuthAuthorize({
    serverId: server.id,
    onAuthComplete: () => {
      setOpen(false);
      onAuthComplete?.();
    },
  });

  const handleClose = useCallback(() => {
    setOpen(false);
    setUserSelectedScopes(null);
    resetAuth();
  }, [resetAuth]);

  const handleScopesChange = useCallback(
    (scopes: string[]) => {
      setUserSelectedScopes(scopes);
      // Clear stale authorization URL since scopes changed
      resetAuth();
    },
    [resetAuth]
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpen(true);
      } else {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetTrigger render={children} />
      <SheetContent className="w-full px-2" tabIndex={-1}>
        {!!open && (
          <ScrollArea className="h-full w-full" tabIndex={-1}>
            <SheetHeader>
              <SheetTitle className="mt-2">Authorize Server</SheetTitle>
              <SheetDescription>
                Complete the authorization process to connect your server.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-2 px-4">
              <OAuthAuthorizationScreen
                authorizationUrl={authorizationUrl}
                isPolling={isPolling}
                onAuthorize={authorize}
                onCompleteLater={handleClose}
                onScopesChange={handleScopesChange}
                scopesSupported={scopesSupported}
                selectedScopes={selectedScopes}
              />
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
};
